import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  message,
  Typography,
  Avatar,
  Tag,
  Switch
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import PageLayout from '../../../components/layout/PageLayout';
import subscriptionService from '../../../services/subscriptionService';
import { useUser } from '../../../context/UserContext';

const { Title, Text } = Typography;
const { Search } = Input;

// Draggable row component
const DraggableRow = ({ index, moveRow, className, style, ...restProps }) => {
  const ref = React.useRef(null);
  
  const [{ handlerId }, drop] = useDrop({
    accept: 'row',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'row',
    item: () => {
      return { index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  return (
    <tr
      ref={ref}
      className={`${className} ${isDragging ? 'dragging' : ''}`}
      style={{ cursor: 'move', opacity, ...style }}
      data-handler-id={handlerId}
      {...restProps}
    />
  );
};

const PlanList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} plans`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: ''
  });

  const getPlans = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        search: filters.search,
        ...params
      };
      
      console.log('Fetching plans with params:', requestParams);
      const response = await subscriptionService.getAllSubscriptionPlan(requestParams);
      console.log('Plans API Response:', response);
      
      if (response.data && response.data.status) {
        const plansData = response.data.data?.data || [];
        const paginationData = response.data.data?.pagination || {};
        
        console.log('Plans data:', plansData);
        console.log('Pagination data:', paginationData);
        
        // Ensure plansData is an array and add order property
        if (Array.isArray(plansData)) {
          const plansWithOrder = plansData.map((plan, index) => ({
            ...plan,
            order: index + 1
          }));
          setPlans(plansWithOrder);
          setPagination(prev => ({
            ...prev,
            total: paginationData.total || plansData.length,
            current: paginationData.currentPage || prev.current
          }));
        } else {
          console.error('Plans data is not an array:', plansData);
          setPlans([]);
        }
      } else {
        message.error('Failed to fetch subscription plans');
        setPlans([]);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      message.error('Failed to fetch subscription plans');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [filters.search, pagination.pageSize, pagination.current]);

  // Load plans data
  useEffect(() => {
    getPlans();
  }, [getPlans]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getPlans({
      search: value,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    getPlans({
      currentPage: current,
      perPage: pageSize,
      search: filters.search
    });
  };

  // Handle add plan
  const handleAddPlan = () => {
    navigate('/subscriptions/plan/addPlan');
  };

  // Handle edit plan
  const handleEditPlan = (planId) => {
    navigate(`/subscriptions/plan/editPlan/${planId}`);
  };

  // Handle drag and drop
  const moveRow = useCallback((dragIndex, hoverIndex) => {
    setPlans(prevPlans => {
      const newPlans = [...prevPlans];
      const draggedPlan = newPlans[dragIndex];
      
      // Remove the dragged item
      newPlans.splice(dragIndex, 1);
      // Insert it at the new position
      newPlans.splice(hoverIndex, 0, draggedPlan);
      
      // Update order for all items
      return newPlans.map((plan, index) => ({
        ...plan,
        order: index + 1
      }));
    });
  }, []);

  // Handle save plan order
  const handleSavePlanOrder = async () => {
    try {
      setLoading(true);
      const response = await subscriptionService.setSubscriptionPlanOrder(plans);
      
      if (response.data && response.data.status) {
        message.success(response.data.message || 'Plan order saved successfully');
      } else {
        message.error('Failed to save plan order');
      }
    } catch (error) {
      console.error('Error saving plan order:', error);
      message.error('Failed to save plan order');
    } finally {
      setLoading(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (index) => {
    try {
      const plan = plans[index];
      const newStatus = plan.plan_is_active === 'Y' ? 'N' : 'Y';
      
      const response = await subscriptionService.setSubscriptionStatus({
        plan_id: plan.plan_id,
        status: newStatus
      });
      
      if (response.data && response.data.status) {
        setPlans(prevPlans => {
          const newPlans = [...prevPlans];
          newPlans[index].plan_is_active = newStatus;
          return newPlans;
        });
        message.success(response.data.message || 'Status changed successfully');
      } else {
        message.error(response.data.message || 'Failed to change status');
      }
    } catch (error) {
      console.error('Error changing status:', error);
      message.error('Failed to change status');
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      width: 80,
      render: (text) => (
        <Text style={{ fontSize: '14px', fontWeight: 'bold' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Plan Name',
      dataIndex: 'plan_name',
      key: 'plan_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.plan_name.localeCompare(b.plan_name),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Years',
      dataIndex: 'years',
      key: 'years',
      width: 80,
      render: (text) => (
        <Text style={{ fontSize: '14px' }}>
          {text || 0}
        </Text>
      )
    },
    {
      title: 'Months',
      dataIndex: 'months',
      key: 'months',
      width: 80,
      render: (text) => (
        <Text style={{ fontSize: '14px' }}>
          {text || 0}
        </Text>
      )
    },
    {
      title: 'Days',
      dataIndex: 'days',
      key: 'days',
      width: 80,
      render: (text) => (
        <Text style={{ fontSize: '14px' }}>
          {text || 0}
        </Text>
      )
    },
    {
      title: 'Plan Amount',
      dataIndex: 'plan_amt',
      key: 'plan_amt',
      width: 120,
      render: (text) => (
        <Text style={{ fontSize: '14px', fontWeight: 'bold', color: '#1890ff' }}>
          ₹{text}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'plan_is_active',
      key: 'plan_is_active',
      width: 100,
      render: (status, record, index) => (
        <Switch
          checked={status === 'Y'}
          onChange={() => handleStatusChange(index)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditPlan(record.plan_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage Subscription Plans</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Subscription Plan List"
      desc="Manage subscription plans and their order"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              Subscription Plans ({pagination.total})
            </Title>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSavePlanOrder}
                loading={loading}
                size="large"
              >
                Save Plan Order
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddPlan}
                size="large"
              >
                Add Plan
              </Button>
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Search
              placeholder="Search plans..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <DndProvider backend={HTML5Backend}>
          <Table
            columns={columns}
            dataSource={plans}
            rowKey="plan_id"
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
            size="middle"
            components={{
              body: {
                row: DraggableRow,
              },
            }}
            onRow={(record, index) => ({
              index,
              moveRow,
            })}
            locale={{ 
              emptyText: 'No subscription plans found',
              filterConfirm: 'OK',
              filterReset: 'Reset',
              filterEmptyText: 'No filters'
            }}
          />
        </DndProvider>
      </Card>
    </PageLayout>
  );
};

export default PlanList;